/**
 * useFirestore.js
 * Generic Firestore CRUD operations + real-time subscriptions
 *
 * @usage
 * const { data, loading, error, refetch } = useFirestoreDoc('users', userId);
 * const { data, loading, error } = useFirestoreQuery('classes', where('teacherId', '==', uid));
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  addDoc, onSnapshot, query, where, orderBy, limit, serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebaseConfig';

/**
 * Subscribe to a single document (real-time)
 */
export function useFirestoreDoc(collectionName, docId, options = {}) {
  const { enabled = true } = options;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !docId) {
      setLoading(false);
      return;
    }

    const docRef = doc(db, collectionName, docId);
    const unsubscribe = onSnapshot(docRef,
      (snap) => {
        if (snap.exists()) {
          setData({ key: snap.id, ...snap.data() });
        } else {
          setData(null);
        }
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error(`[useFirestoreDoc] Error on ${collectionName}/${docId}:`, err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [collectionName, docId, enabled]);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, collectionName, docId));
      setData(snap.exists() ? { key: snap.id, ...snap.data() } : null);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [collectionName, docId]);

  return { data, loading, error, refetch };
}

/**
 * Subscribe to a collection or query (real-time)
 * @param {...QueryConstraint} constraints - Firestore query constraints
 */
export function useFirestoreQuery(collectionName, ...constraints) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const unsubscribeRef = useRef(null);
  const constraintsJsonRef = useRef('');

  useEffect(() => {
    const newJson = JSON.stringify(constraints);
    // Skip if constraints unchanged — avoid unnecessary re-subscription
    if (newJson === constraintsJsonRef.current && unsubscribeRef.current) {
      return;
    }
    constraintsJsonRef.current = newJson;

    const colRef = collection(db, collectionName);
    const q = constraints.length > 0 ? query(colRef, ...constraints) : colRef;

    setLoading(true);
    if (unsubscribeRef.current) unsubscribeRef.current();
    unsubscribeRef.current = onSnapshot(q,
      (snap) => {
        const items = snap.docs.map(d => ({ key: d.id, ...d.data() }));
        setData(items);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error(`[useFirestoreQuery] Error on ${collectionName}:`, err);
        setError(err);
        setLoading(false);
      }
    );

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [collectionName]);

  return { data, loading, error, setData };
}

/**
 * One-time fetch (no subscription)
 */
export async function fetchFirestoreDoc(collectionName, docId) {
  try {
    const snap = await getDoc(doc(db, collectionName, docId));
    return snap.exists() ? { key: snap.id, ...snap.data() } : null;
  } catch (err) {
    console.error(`[fetchFirestoreDoc] Error:`, err);
    throw err;
  }
}

/**
 * One-time fetch collection/query
 */
export async function fetchFirestoreQuery(collectionName, ...constraints) {
  try {
    const colRef = collection(db, collectionName);
    const q = constraints.length > 0 ? query(colRef, ...constraints) : colRef;
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ key: d.id, ...d.data() }));
  } catch (err) {
    console.error(`[fetchFirestoreQuery] Error:`, err);
    throw err;
  }
}

/**
 * Create a new document (auto-generate ID)
 */
export async function createFirestoreDoc(collectionName, data) {
  try {
    const docRef = await addDoc(collection(db, collectionName), {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return docRef.id;
  } catch (err) {
    console.error(`[createFirestoreDoc] Error:`, err);
    throw err;
  }
}

/**
 * Update an existing document
 */
export async function updateFirestoreDoc(collectionName, docId, data) {
  try {
    await updateDoc(doc(db, collectionName, docId), {
      ...data,
      updatedAt: serverTimestamp()
    });
  } catch (err) {
    console.error(`[updateFirestoreDoc] Error:`, err);
    throw err;
  }
}

/**
 * Set a document (create or overwrite)
 */
export async function setFirestoreDoc(collectionName, docId, data, merge = true) {
  try {
    await setDoc(doc(db, collectionName, docId), {
      ...data,
      updatedAt: serverTimestamp()
    }, { merge });
  } catch (err) {
    console.error(`[setFirestoreDoc] Error:`, err);
    throw err;
  }
}

/**
 * Delete a document
 */
export async function deleteFirestoreDoc(collectionName, docId) {
  try {
    await deleteDoc(doc(db, collectionName, docId));
  } catch (err) {
    console.error(`[deleteFirestoreDoc] Error:`, err);
    throw err;
  }
}

/**
 * Batch write multiple documents
 */
export async function batchWriteFirestore(operations) {
  // operations = [{ type: 'set'|'update'|'add', collection, id?, data }]
  try {
    const { writeBatch } = await import('firebase/firestore');
    const batch = writeBatch(db);

    for (const op of operations) {
      const ref = op.id
        ? doc(db, op.collection, op.id)
        : doc(collection(db, op.collection));

      const data = {
        ...op.data,
        updatedAt: serverTimestamp()
      };

      if (op.type === 'add') {
        batch.set(ref, { ...data, createdAt: serverTimestamp() });
      } else if (op.type === 'update') {
        batch.update(ref, data);
      } else {
        batch.set(ref, data, { merge: true });
      }
    }

    await batch.commit();
  } catch (err) {
    console.error(`[batchWriteFirestore] Error:`, err);
    throw err;
  }
}
